import { useQuery } from '@tanstack/react-query';
import { History, Loader2 } from 'lucide-react';
import {
  Badge,
  Card,
  EmptyState,
  ErrorNote,
  Skeleton,
} from '../../components/ui';
import { api } from '../../lib/api';
import { formatDateTime, formatNumber, percentOf } from '../../lib/format';
import type { Campaign, CampaignStatus, Page } from '../../lib/types';

const STATUS_TONE: Record<
  CampaignStatus,
  'neutral' | 'brand' | 'good' | 'warn' | 'danger'
> = {
  queued: 'neutral',
  sending: 'brand',
  completed: 'good',
  failed: 'danger',
};

export function HistoryTab() {
  const campaigns = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => api.get<Page<Campaign>>('/api/notifications/campaigns', { limit: 25 }),
    // Poll only while something is in flight — a finished list is static.
    refetchInterval: (query) =>
      query.state.data?.items.some(
        (campaign) =>
          campaign.status === 'sending' || campaign.status === 'queued',
      )
        ? 3_000
        : false,
  });

  if (campaigns.isError) return <ErrorNote error={campaigns.error} />;

  if (campaigns.isPending) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-24" />
        ))}
      </div>
    );
  }

  if (!campaigns.data?.items.length) {
    return (
      <Card>
        <EmptyState
          icon={<History size={28} />}
          title="Nothing sent yet"
          description="Campaigns you send will show up here with their delivery counts."
        />
      </Card>
    );
  }

  return (
    <ul className="space-y-3">
      {campaigns.data.items.map((campaign) => (
        <li key={campaign.id}>
          <CampaignCard campaign={campaign} />
        </li>
      ))}
    </ul>
  );
}

function CampaignCard({ campaign }: { campaign: Campaign }) {
  const processed =
    campaign.sent_count + campaign.failed_count + campaign.skipped_count;
  const inFlight = campaign.status === 'sending' || campaign.status === 'queued';

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-ink">
              {campaign.template_name ?? campaign.template_key ?? 'Notification'}
            </h3>
            <Badge
              tone={STATUS_TONE[campaign.status]}
              icon={
                inFlight ? <Loader2 size={11} className="animate-spin" /> : undefined
              }
            >
              {campaign.status}
            </Badge>
            {campaign.app_key && <Badge>{campaign.app_key}</Badge>}
            {campaign.message && (
              <Badge tone="brand">
                {campaign.filters?.device_id ? 'Direct' : 'Custom'}
              </Badge>
            )}
          </div>
          {campaign.message && (
            <p className="mt-1 line-clamp-2 text-xs text-ink-2">
              {campaign.message.body}
            </p>
          )}
          <p className="mt-1 text-xs text-ink-3">
            {formatDateTime(campaign.created_at)}
          </p>
        </div>

        <p className="tnum text-sm text-ink-2">
          {formatNumber(processed)} / {formatNumber(campaign.total_recipients)}
        </p>
      </div>

      <div
        className="mt-3 h-1.5 overflow-hidden rounded-full bg-raised"
        role="progressbar"
        aria-valuenow={processed}
        aria-valuemin={0}
        aria-valuemax={campaign.total_recipients}
      >
        <div
          className={campaign.status === 'failed' ? 'h-full bg-danger' : 'h-full bg-brand'}
          style={{ width: percentOf(processed, campaign.total_recipients) }}
        />
      </div>

      <dl className="mt-3 grid grid-cols-3 gap-2 text-xs">
        <Stat label="Delivered" value={campaign.sent_count} />
        <Stat label="Failed" value={campaign.failed_count} />
        <Stat label="Skipped" value={campaign.skipped_count} />
      </dl>

      {campaign.error && (
        <p className="mt-3 rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-xs text-danger">
          {campaign.error}
        </p>
      )}

      {campaign.filters && (
        <p className="mt-3 truncate text-xs text-ink-3" title={describeFilters(campaign.filters)}>
          Audience: {describeFilters(campaign.filters)}
        </p>
      )}
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg bg-raised px-2 py-1.5">
      <dt className="text-[11px] text-ink-3">{label}</dt>
      <dd className="tnum mt-0.5 font-medium text-ink">{formatNumber(value)}</dd>
    </div>
  );
}

/** Renders the stored filter back as the phrase an operator would recognise. */
function describeFilters(filters: Record<string, unknown>): string {
  const parts = Object.entries(filters)
    .filter(([key]) => key !== 'app_key')
    .map(([key, value]) => `${key.replace(/_/g, ' ')}: ${String(value)}`);
  return parts.length > 0 ? parts.join(' · ') : 'everyone in the app';
}
